import MondialRelaySettingsCard from "@/components/admin/MondialRelaySettingsCard";
import { useEffect, useState } from "react";
import { Loader2, MapPin, Pencil, Plus, Scale, Trash2, Truck, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";
import AdminAssociations from "@/pages/admin/AdminAssociations";

const EMPTY = { name: "", description: "", price: "", free_above: "", active: true, zone: "all", postal_prefixes: "", scheduled: false, brackets: [] };
const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

const ZONE_LABELS = { all: "Partout", metro: "France métropolitaine", postal: "Codes postaux précis" };

export default function AdminShipping() {
  const [methods, setMethods] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/shipping").then(({ data }) => setMethods(data)).catch(() => setMethods([]));
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm(EMPTY);
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (m) => {
    setForm({
      name: m.name,
      description: m.description || "",
      price: (m.price_cents / 100).toFixed(2),
      free_above: m.free_above_cents ? (m.free_above_cents / 100).toFixed(0) : "",
      active: m.active,
      zone: m.zone || "all",
      postal_prefixes: (m.postal_prefixes || []).join(", "),
      scheduled: Boolean(m.scheduled),
      brackets: (m.weight_brackets || []).map((b) => ({ max_weight_g: String(b.max_weight_g), price: (b.price_cents / 100).toFixed(2) })),
    });
    setEditingId(m.method_id);
    setFormOpen(true);
  };

  const setBracket = (i, key, value) =>
    setForm((f) => ({ ...f, brackets: f.brackets.map((b, j) => (j === i ? { ...b, [key]: value } : b)) }));
  const addBracket = () => setForm((f) => ({ ...f, brackets: [...f.brackets, { max_weight_g: "", price: "" }] }));
  const removeBracket = (i) => setForm((f) => ({ ...f, brackets: f.brackets.filter((_, j) => j !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const brackets = form.brackets
      .map((b) => ({ max_weight_g: parseInt(b.max_weight_g, 10), price: parseFloat(String(b.price).replace(",", ".")) }))
      .filter((b) => b.max_weight_g > 0 && !Number.isNaN(b.price) && b.price >= 0);
    if (form.brackets.length > 0 && brackets.length !== form.brackets.length) {
      toast.error("Chaque tranche doit avoir un poids (g) et un prix valides");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      description: form.description,
      price: parseFloat(String(form.price).replace(",", ".")) || 0,
      free_above: form.free_above ? parseFloat(String(form.free_above).replace(",", ".")) : null,
      active: form.active,
      zone: form.zone,
      postal_prefixes: form.postal_prefixes,
      scheduled: form.zone === "postal" && form.scheduled,
      weight_brackets: brackets,
    };
    try {
      if (editingId) {
        await api.put(`/admin/shipping/${editingId}`, payload);
        toast.success("Mode de livraison mis à jour");
      } else {
        await api.post("/admin/shipping", payload);
        toast.success("Mode de livraison créé — disponible dans le panier");
      }
      setFormOpen(false);
      setEditingId(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Supprimer le mode « ${m.name} » ?`)) return;
    try {
      await api.delete(`/admin/shipping/${m.method_id}`);
      toast.success("Mode de livraison supprimé");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-shipping" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-bark">Livraison</h1>
          <p className="mt-1 text-sm text-bark-soft">Les modes actifs sont proposés aux clients dans le panier.</p>
        </div>
        <button
          data-testid="new-shipping-button"
          onClick={openCreate}
          className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95"
        >
          <Plus size={16} /> Nouveau mode
        </button>
      </div>

      <MondialRelaySettingsCard />

      {formOpen && (
        <form data-testid="shipping-form" onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-xl font-semibold text-bark">{editingId ? "Modifier le mode" : "Nouveau mode de livraison"}</h2>
            <button type="button" data-testid="shipping-form-close" onClick={() => setFormOpen(false)} className="text-stone-mute hover:text-bark" aria-label="Fermer">
              <X size={18} />
            </button>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="sh-name" className="mb-1.5 block text-sm font-medium text-bark">Nom *</label>
              <input id="sh-name" data-testid="sh-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder="Livraison standard" />
            </div>
            <div>
              <label htmlFor="sh-description" className="mb-1.5 block text-sm font-medium text-bark">Description</label>
              <input id="sh-description" data-testid="sh-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} placeholder="48 à 72 h, Colissimo suivi" />
            </div>
            <div>
              <label htmlFor="sh-price" className="mb-1.5 block text-sm font-medium text-bark">Prix (€) *</label>
              <input id="sh-price" data-testid="sh-price" required type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputClass} placeholder="5,90" />
              <p className="mt-1 text-xs text-stone-mute">Prix fixe — utilisé si aucune tranche de poids n'est définie.</p>
            </div>
            <div>
              <label htmlFor="sh-free-above" className="mb-1.5 block text-sm font-medium text-bark">Offerte dès (€) — optionnel</label>
              <input id="sh-free-above" data-testid="sh-free-above" type="number" min="0" step="1" value={form.free_above} onChange={(e) => setForm({ ...form, free_above: e.target.value })} className={inputClass} placeholder="50" />
            </div>
            <div>
              <label htmlFor="sh-zone" className="mb-1.5 block text-sm font-medium text-bark">Zone de livraison</label>
              <select id="sh-zone" data-testid="sh-zone" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} className={inputClass}>
                <option value="all">Partout (aucune restriction)</option>
                <option value="metro">France métropolitaine uniquement</option>
                <option value="postal">Codes postaux précis</option>
              </select>
            </div>
            {form.zone === "postal" && (
              <div>
                <label htmlFor="sh-postal-prefixes" className="mb-1.5 block text-sm font-medium text-bark">Codes postaux / départements *</label>
                <input id="sh-postal-prefixes" data-testid="sh-postal-prefixes" required value={form.postal_prefixes} onChange={(e) => setForm({ ...form, postal_prefixes: e.target.value })} className={inputClass} placeholder="04, 84, 43 ou 04210, 04300…" />
                <p className="mt-1 text-xs text-stone-mute">Séparés par des virgules. « 04 » couvre tout le département.</p>
                <label className="mt-3 flex cursor-pointer items-start gap-2 text-sm text-bark">
                  <input type="checkbox" data-testid="sh-scheduled" checked={form.scheduled} onChange={(e) => setForm({ ...form, scheduled: e.target.checked })} className="mt-0.5 h-4 w-4 accent-amber-deep" />
                  <span>Livraison sur rendez-vous : le client choisit un créneau de votre agenda (Admin → Tournées)</span>
                </label>
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-xl border border-amber-900/10 bg-amber-50/40 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-sm font-medium text-bark">
                <Scale size={16} className="text-honey" /> Tarifs par poids (optionnel)
              </p>
              <button
                type="button"
                data-testid="sh-add-bracket"
                onClick={addBracket}
                className="flex items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-4 py-1.5 text-xs font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep"
              >
                <Plus size={13} /> Ajouter une tranche
              </button>
            </div>
            <p className="text-xs text-stone-mute">
              Si des tranches sont définies, le prix est calculé selon le poids total du panier (le « Poids envoi » de chaque produit, réglé dans sa fiche admin). Au-delà de la dernière tranche, c'est son tarif qui s'applique.
            </p>
            {form.brackets.map((b, i) => (
              <div key={i} data-testid={`sh-bracket-${i}`} className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-bark-soft">Jusqu'à</span>
                <input
                  data-testid={`sh-bracket-weight-${i}`}
                  type="number"
                  min="1"
                  step="1"
                  value={b.max_weight_g}
                  onChange={(e) => setBracket(i, "max_weight_g", e.target.value)}
                  className="w-28 rounded-xl border border-amber-900/20 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-amber-deep"
                  placeholder="500"
                />
                <span className="text-xs text-bark-soft">g →</span>
                <input
                  data-testid={`sh-bracket-price-${i}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={b.price}
                  onChange={(e) => setBracket(i, "price", e.target.value)}
                  className="w-28 rounded-xl border border-amber-900/20 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-amber-deep"
                  placeholder="5,90"
                />
                <span className="text-xs text-bark-soft">€</span>
                <button
                  type="button"
                  data-testid={`sh-bracket-remove-${i}`}
                  onClick={() => removeBracket(i)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10"
                  aria-label="Retirer la tranche"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>

          <label data-testid="sh-active-label" className="flex cursor-pointer items-center gap-3 text-sm font-medium text-bark">
            <input data-testid="sh-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-amber-deep" />
            Mode actif (visible dans le panier)
          </label>
          <button
            data-testid="sh-submit"
            type="submit"
            disabled={saving}
            className="rounded-full bg-amber-deep px-6 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : editingId ? "Enregistrer" : "Créer le mode"}
          </button>
        </form>
      )}

      {!methods ? (
        <p data-testid="shipping-loading" className="py-16 text-center font-serif text-xl text-bark-soft">Chargement…</p>
      ) : methods.length === 0 ? (
        <p data-testid="shipping-empty" className="py-16 text-center font-serif text-xl text-bark-soft">Aucun mode de livraison.</p>
      ) : (
        <div className="space-y-4">
          {methods.map((m) => (
            <div key={m.method_id} data-testid={`shipping-row-${m.method_id}`} className="flex flex-wrap items-center gap-4 rounded-2xl border border-amber-800/15 bg-white p-5 shadow-sm">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${m.active ? "bg-sage-light text-sage" : "bg-sand text-stone-mute"}`}>
                <Truck size={22} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg font-semibold text-bark">
                  {m.name}
                  {!m.active && <span className="ml-2 rounded-full bg-sand px-2.5 py-0.5 text-xs font-medium text-stone-mute">inactif</span>}
                </p>
                {m.description && <p className="truncate text-sm text-bark-soft">{m.description}</p>}
                <p data-testid={`shipping-price-${m.method_id}`} className="mt-1 text-xs uppercase tracking-[0.12em] text-stone-mute">
                  {m.price_cents === 0 ? "Gratuit" : formatPrice(m.price_cents)}
                  {m.free_above_cents ? ` · offerte dès ${formatPrice(m.free_above_cents)}` : ""}
                </p>
                {m.weight_brackets?.length > 0 && (
                  <p data-testid={`shipping-brackets-${m.method_id}`} className="mt-1 flex items-center gap-1.5 text-xs text-amber-deep">
                    <Scale size={12} />
                    Selon poids : {[...m.weight_brackets].sort((a, b) => a.max_weight_g - b.max_weight_g).map((b) => `≤ ${b.max_weight_g} g → ${formatPrice(b.price_cents)}`).join(" · ")}
                  </p>
                )}
                <p data-testid={`shipping-zone-${m.method_id}`} className="mt-1 flex items-center gap-1.5 text-xs text-sage">
                  <MapPin size={12} />
                  {m.zone === "postal"
                    ? `Codes : ${(m.postal_prefixes || []).join(", ")}`
                    : ZONE_LABELS[m.zone || "all"]}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  data-testid={`edit-shipping-${m.method_id}`}
                  onClick={() => openEdit(m)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-800/20 text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep"
                  aria-label={`Modifier ${m.name}`}
                >
                  <Pencil size={15} />
                </button>
                <button
                  data-testid={`delete-shipping-${m.method_id}`}
                  onClick={() => handleDelete(m)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10"
                  aria-label={`Supprimer ${m.name}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <AdminAssociations />
    </div>
  );
}
